import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Bot, Send, User, AlertTriangle, Phone, ChevronRight, Loader2,
  MessageSquare, Sparkles, Clock, HelpCircle, Heart, Pill, Calendar, X,
  ThumbsUp, ThumbsDown, RefreshCw
} from "lucide-react";

export default function AICareAssistant({ isOpen, onClose }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [quickAnswers, setQuickAnswers] = useState(null);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [escalateReason, setEscalateReason] = useState("");
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadSessions();
      loadQuickAnswers();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadSessions = async () => {
    try {
      const res = await api.get("/assistant/sessions");
      setSessions(res.data.sessions || []);
    } catch (error) {
      console.error("Error loading sessions:", error);
    }
  };

  const loadQuickAnswers = async () => {
    try {
      const res = await api.get("/assistant/quick-answers");
      setQuickAnswers(res.data);
    } catch (error) {
      console.error("Error loading quick answers:", error);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      setIsLoading(true);
      const res = await api.get(`/assistant/sessions/${sessionId}`);
      setCurrentSession(res.data.session);
      setMessages(res.data.messages || []);
    } catch (error) {
      toast.error("Failed to load conversation");
    } finally {
      setIsLoading(false);
    }
  };

  const startNewChat = () => {
    setCurrentSession(null);
    setMessages([]);
    setInputMessage("");
  };

  const sendMessage = async (text = inputMessage) => {
    if (!text.trim()) return;
    
    const messageText = text.trim();
    setInputMessage("");
    setIsSending(true);
    
    // Add user message optimistically
    const tempUserMessage = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMessage]);
    
    try {
      const res = await api.post("/assistant/chat", {
        message: messageText,
        session_id: currentSession?.id
      });
      
      // Update session if new
      if (!currentSession) {
        setCurrentSession({ id: res.data.session_id });
        loadSessions();
      }
      
      // Add assistant response
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempUserMessage.id),
        { ...tempUserMessage, id: `user-${Date.now()}` },
        res.data.message
      ]);
      
      // Check for escalation
      if (res.data.escalated) {
        toast.warning("Your message has been flagged for care team review", {
          description: res.data.escalation_reason,
          duration: 5000
        });
      }
      
    } catch (error) {
      toast.error("Failed to send message");
      setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      setInputMessage(messageText);
    } finally {
      setIsSending(false);
    }
  };

  const handleQuickQuestion = (question) => {
    sendMessage(question);
  };

  const handleEscalate = async () => {
    if (!currentSession || !escalateReason.trim()) return;
    
    try {
      await api.post(`/assistant/sessions/${currentSession.id}/escalate`, {
        reason: escalateReason
      });
      toast.success("Your request has been sent to your care team");
      setShowEscalateDialog(false);
      setEscalateReason("");
      loadSession(currentSession.id);
    } catch (error) {
      toast.error("Failed to escalate");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ai-assistant-modal">
      <Card className="w-full max-w-2xl h-[80vh] flex flex-col bg-white shadow-2xl">
        {/* Header */}
        <CardHeader className="border-b bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <CardTitle className="text-lg">Care Assistant</CardTitle>
                <p className="text-teal-100 text-xs">Powered by AI • Available 24/7</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentSession && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={startNewChat}
                  className="text-white hover:bg-white/20"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  New Chat
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={onClose}
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
          {/* Messages or Welcome Screen */}
          {messages.length === 0 && !isLoading ? (
            <div className="flex-1 p-6 overflow-auto">
              {/* Welcome */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800">Hi {user?.name?.split(" ")[0] || "there"}!</h3>
                <p className="text-slate-500 mt-1">I&apos;m here to help with your questions about care, medications, and more.</p>
              </div>

              {/* Quick Questions */}
              {quickAnswers && (
                <div className="space-y-4">
                  {quickAnswers.categories.map((category, idx) => (
                    <div key={idx}>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                        {category.name === "Medications" && <Pill className="w-4 h-4 text-amber-500" />}
                        {category.name === "Symptoms" && <Heart className="w-4 h-4 text-red-500" />}
                        {category.name === "Care" && <Calendar className="w-4 h-4 text-blue-500" />}
                        {category.name === "Support" && <HelpCircle className="w-4 h-4 text-purple-500" />}
                        {category.name}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {category.questions.map((q, qIdx) => (
                          <Button
                            key={qIdx}
                            variant="outline"
                            size="sm"
                            onClick={() => handleQuickQuestion(q)}
                            className="text-xs border-slate-200 hover:border-teal-500 hover:text-teal-600"
                          >
                            {q}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Recent Chats */}
              {sessions.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Recent Conversations</h4>
                  <div className="space-y-2">
                    {sessions.slice(0, 3).map(session => (
                      <button
                        key={session.id}
                        onClick={() => loadSession(session.id)}
                        className="w-full p-3 text-left bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 truncate">{session.title}</span>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(session.updated_at).toLocaleDateString()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Messages List */
            <ScrollArea className="flex-1 p-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          msg.role === "user" ? "bg-teal-100" :
                          msg.role === "staff" ? "bg-blue-100" :
                          msg.role === "system" ? "bg-orange-100" : "bg-slate-100"
                        }`}>
                          {msg.role === "user" ? (
                            <User className="w-4 h-4 text-teal-600" />
                          ) : msg.role === "staff" ? (
                            <User className="w-4 h-4 text-blue-600" />
                          ) : msg.role === "system" ? (
                            <AlertTriangle className="w-4 h-4 text-orange-600" />
                          ) : (
                            <Bot className="w-4 h-4 text-slate-600" />
                          )}
                        </div>
                        <div>
                          <div className={`p-3 rounded-lg ${
                            msg.role === "user" ? "bg-teal-600 text-white" :
                            msg.role === "staff" ? "bg-blue-50 text-slate-800" :
                            msg.role === "system" ? "bg-orange-50 text-orange-800" :
                            "bg-slate-100 text-slate-800"
                          }`}>
                            {msg.role === "staff" && msg.metadata?.staff_name && (
                              <p className="text-xs font-semibold text-blue-600 mb-1">
                                {msg.metadata.staff_name} ({msg.metadata.staff_role})
                              </p>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-400">
                              {formatTime(msg.created_at)}
                            </span>
                            {msg.escalated && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">
                                Escalated
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {isSending && (
                    <div className="flex justify-start">
                      <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-100" />
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce delay-200" />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
          )}

          {/* Input Area */}
          <div className="p-4 border-t bg-white">
            {/* Escalate Button */}
            {currentSession && messages.length > 0 && (
              <div className="flex justify-center mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowEscalateDialog(true)}
                  className="text-orange-600 border-orange-200 hover:bg-orange-50"
                >
                  <Phone className="w-4 h-4 mr-1" />
                  Talk to Care Team
                </Button>
              </div>
            )}
            
            <div className="flex gap-2">
              <Textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your question..."
                className="resize-none h-12 min-h-[48px]"
                disabled={isSending}
              />
              <Button
                onClick={() => sendMessage()}
                disabled={!inputMessage.trim() || isSending}
                className="bg-teal-600 hover:bg-teal-700 h-12"
              >
                {isSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            
            <p className="text-xs text-slate-400 text-center mt-2">
              For emergencies, call <span className="font-semibold">911</span> or <span className="font-semibold">703-578-7108</span>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Escalate Dialog */}
      <Dialog open={showEscalateDialog} onOpenChange={setShowEscalateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-orange-500" />
              Request Human Support
            </DialogTitle>
            <DialogDescription>
              Your care team will be notified and will respond as soon as possible.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm font-medium text-slate-700">
              What do you need help with?
            </label>
            <Textarea
              value={escalateReason}
              onChange={(e) => setEscalateReason(e.target.value)}
              placeholder="Please describe your concern..."
              className="mt-2 h-24"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEscalate}
              disabled={!escalateReason.trim()}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to Care Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
